import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ApiClient from "../../api-client";
import Config from "../../../config";
import Util from "../../util";
import UserPermissions from "../../../modules/auth/UserPermissions";

const PumaUpdateButton = ({ appliance, isLatestFirmwareVersion, onUpdateInitiated }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [error, setError] = useState(null);

  const imgBasePath = `${Config.baseDir}components/dashboard-widgets/connectivity/images`;

  // Check if device is Puma (type 1.x.y.z)
  const isPumaDevice = appliance.type && appliance.type.startsWith("1.");
  // Check both connectedStatus and connectionState fields
  const isOnline = appliance.connectedStatus === "Connected" || 
                   appliance.connectionState === "Connected" ||
                   appliance.azureTwinConnectedStatus === "Connected";
  const canInitiateUpdate = UserPermissions.allowSendFirmwareRequest();
  
  // Debug logging
  console.log("PumaUpdateButton - Device info:", {
    deviceId: appliance.deviceId,
    type: appliance.type,
    connectedStatus: appliance.connectedStatus,
    connectionState: appliance.connectionState,
    azureTwinConnectedStatus: appliance.azureTwinConnectedStatus,
    isPumaDevice,
    isOnline,
    canInitiateUpdate
  });

  useEffect(() => {
    if (isPumaDevice && isOnline) {
      // Always check for Puma updates regardless of isLatestFirmwareVersion
      checkUpdateAvailability();
    }
  }, [appliance.deviceId, isPumaDevice, isOnline]);

  const checkUpdateAvailability = async () => {
    setIsChecking(true);
    setError(null);
    
    try {
      const response = await ApiClient.instance.get(
        `/appliances/${appliance.deviceId}/puma/firmware-update/available`
      );
      
      const data = await ApiClient.asJson(response);
      if (data) {
        setUpdateAvailable(data.updateAvailable);
      }
    } catch (err) {
      console.error("Error checking Puma update availability:", err);
      setError("Failed to check update availability");
    } finally {
      setIsChecking(false);
    }
  };

  const initiateFirmwareUpdate = async () => {
    if (!window.confirm("Are you sure you want to initiate a firmware update?")) {
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const response = await ApiClient.instance.post(
        `/appliances/${appliance.deviceId}/puma/firmware-update`,
        {}
      );

      const data = await ApiClient.asJson(response);
      if (data) {
        setUpdateStatus("Update initiated successfully");
        
        // Track event
        Util.trackEventWithProperties("PUMA_FIRMWARE_UPDATE", "Initiate Puma Update", {
          deviceId: appliance.deviceId,
          serial: appliance.serial,
          model: appliance.model,
          type: appliance.type,
          currentVersion: appliance.pumaVersion,
          productFamily: appliance.productFamily
        });

        if (onUpdateInitiated) {
          onUpdateInitiated(data);
        }

        // Check status after a delay
        setTimeout(() => checkUpdateStatus(), 5000);
      }
    } catch (err) {
      console.error("Error initiating Puma firmware update:", err);
      setError("Failed to initiate firmware update");
      setUpdateStatus(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const checkUpdateStatus = async () => {
    try {
      const response = await ApiClient.instance.get(
        `/appliances/${appliance.deviceId}/puma/firmware-update/status`
      );

      const data = await ApiClient.asJson(response);
      if (data) {
        setUpdateStatus(data.statusMessage || data.status);
      }
    } catch (err) {
      console.error("Error checking update status:", err);
    }
  };

  // Don't show button if not a Puma device or user doesn't have permission
  if (!isPumaDevice || !canInitiateUpdate) {
    return null;
  }

  // Show offline warning if device is offline
  if (!isOnline) {
    return (
      <a
        className="disabled"
        title="Device must be online to initiate firmware update"
      >
        <img src={`${imgBasePath}/warning_icon.svg`} id="firmware_version" />
      </a>
    );
  }

  // Show loading spinner while checking
  if (isChecking) {
    return <span className="update-checking">Checking...</span>;
  }

  // Show update button if update is available
  if (updateAvailable && !isLatestFirmwareVersion) {
    return (
      <div className="puma-update-container">
        <button
          className="puma-update-btn"
          onClick={initiateFirmwareUpdate}
          disabled={isUpdating}
          title="Click to initiate firmware update"
        >
          {isUpdating ? "Updating..." : "Update"}
        </button>
        {updateStatus && (
          <div className="update-status">{updateStatus}</div>
        )}
        {error && (
          <div className="update-error">{error}</div>
        )}
      </div>
    );
  }

  // Show email link as fallback
  if (!isLatestFirmwareVersion) {
    const frmwarUpdateLink = `mailto:${Config.snowTicketTargetEmail}?subject=Connected Module Update
      &body=Issue: Please update the connected module on this appliance.
      %0D%0AMAC Address: ${appliance.deviceId}
      %0D%0ACustomer Time Zone: `;
    
    return (
      <a
        href={frmwarUpdateLink}
        target="_blank"
        title="Connected Module out of date.\nClick to request update."
      >
        <img src={`${imgBasePath}/frmwr_updt.svg`} id="firmware_version" />
      </a>
    );
  }

  return null;
};

PumaUpdateButton.propTypes = {
  appliance: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    type: PropTypes.string,
    connectedStatus: PropTypes.string,
    pumaVersion: PropTypes.string,
    serial: PropTypes.string,
    model: PropTypes.string,
    productFamily: PropTypes.string
  }).isRequired,
  isLatestFirmwareVersion: PropTypes.bool,
  onUpdateInitiated: PropTypes.func
};

export default PumaUpdateButton;